'use client';

import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  ChevronRightIcon,
  LocationOnIcon,
  CalendarTodayIcon,
  SquareFootIcon,
  AccessTimeIcon,
} from '@/shared/ui/display/EmojiIcon';
import type { VisitCounselView } from '@/entities/visit-counsel';
import { VisitCounselStatusChip, type VisitCounselStatusVariant } from '@/entities/visit-counsel';
import { formatScheduleTimeRange } from '@/shared/lib/timeslot/timeslot.types';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';

dayjs.locale('ko');

function formatDate(date: string): string {
  return dayjs(date).format('M월 D일 (ddd)');
}

export interface CounselCardProps {
  counsel: VisitCounselView;
  variant: VisitCounselStatusVariant;
  onSelect: (id: string) => void;
}

export const CounselCard = React.memo(function CounselCard({
  counsel,
  variant,
  onSelect,
}: CounselCardProps) {
  const isStarted = variant === 'started';

  return (
    <Paper
      onClick={() => onSelect(counsel.id)}
      elevation={isStarted ? 1 : 0}
      sx={{
        p: 2.5,
        mb: 1.5,
        minHeight: 72,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, background-color 0.15s, border-color 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        ...(isStarted && {
          bgcolor: 'action.selected',
        }),
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {formatDate(counsel.scheduleDate)}
            </Typography>
          </Box>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
              {formatScheduleTimeRange(counsel.scheduleTime, 'am-pm')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <VisitCounselStatusChip variant={variant} sx={{ height: 28, fontSize: '0.8125rem' }} />
          <ChevronRightIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <LocationOnIcon sx={{ fontSize: 18, color: 'text.secondary', mt: '2px', flexShrink: 0 }} />
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.9375rem',
          }}
        >
          {counsel.location}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
          <SquareFootIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.875rem' }}>
            {counsel.supplyArea}평
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
});
